import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import UserManagementDateTime from "./UserManagementDateTime";

const setupSteps = (merchant, text) => [
  ["branch", text.branchSetup, Boolean(merchant.hasBranch)],
  ["plan", text.planSetup, Boolean(merchant.hasPlan)],
  ["payment", text.paymentMethodSetup, Boolean(merchant.hasPaymentMethod)]
];

const SetupSteps = ({ steps, text }) => (
  <>
    {steps.map(([key, label, done]) => (
      <span key={key} className={done ? "is-complete" : "is-missing"}>
        <i
          className={`fa-solid ${done ? "fa-circle-check" : "fa-circle-xmark"}`}
          aria-hidden="true"
        />
        <span>{label}</span>
        <small>{done ? text.completed : text.notCompleted}</small>
      </span>
    ))}
  </>
);

const localizedName = (record, language) =>
  record?.[language === "ar" ? "nameAr" : "nameEn"] ||
  record?.nameEn ||
  record?.nameAr ||
  "—";

const SetupRecordDetails = ({ rows }) => (
  <dl className="merchant-setup-record-grid">
    {rows.map(([label, value], index) => (
      <div key={`${label}-${index}`}>
        <dt>{label}</dt>
        <dd>{value || "—"}</dd>
      </div>
    ))}
  </dl>
);

const SetupRecord = ({ icon, title, subtitle, children }) => (
  <details className="merchant-setup-record">
    <summary>
      <i className={`fa-solid ${icon}`} aria-hidden="true" />
      <span><strong>{title}</strong>{subtitle && <small>{subtitle}</small>}</span>
      <i className="fa-solid fa-chevron-down merchant-setup-record-chevron" aria-hidden="true" />
    </summary>
    <div className="merchant-setup-record-body">{children}</div>
  </details>
);

const SetupCollection = ({ icon, title, items, emptyText, renderItem }) => (
  <section className="merchant-setup-collection">
    <header>
      <span><i className={`fa-solid ${icon}`} aria-hidden="true" />{title}</span>
      <strong>{items.length}</strong>
    </header>
    {items.length ? (
      <div className="merchant-setup-records">{items.map(renderItem)}</div>
    ) : (
      <p>{emptyText}</p>
    )}
  </section>
);

const MerchantSetupCollections = ({ merchant, text, language }) => {
  const branches = merchant.setupDetails?.branches || [];
  const plans = merchant.setupDetails?.plans || [];
  const paymentMethods = merchant.setupDetails?.paymentMethods || [];
  const dateValue = (value) => value
    ? <UserManagementDateTime value={value} language={language} />
    : "—";

  return (
    <div className="merchant-setup-collections">
      <SetupCollection
        icon="fa-location-dot"
        title={text.setupBranches}
        items={branches}
        emptyText={text.noSetupBranches}
        renderItem={(branch) => {
          const lat = Number(branch.location?.lat);
          const lng = Number(branch.location?.lng);
          const hasLocation = Number.isFinite(lat) && Number.isFinite(lng) &&
            !(lat === 0 && lng === 0);
          return (
            <SetupRecord
              key={branch.id}
              icon="fa-store"
              title={localizedName(branch, language)}
              subtitle={branch.address}
            >
              <SetupRecordDetails rows={[
                [text.arabicName, branch.nameAr],
                [text.englishName, branch.nameEn],
                [text.branchAddress, branch.address],
                [text.city, localizedName(branch.governorate, language)],
                [text.country, localizedName(branch.country, language)],
                [text.coordinates, hasLocation ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "—"],
                [text.createdAt, dateValue(branch.createdAt)],
                [text.updatedAt, dateValue(branch.updatedAt)]
              ]} />
              {hasLocation && (
                <a
                  className="merchant-setup-map-link"
                  href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                  {text.openInGoogleMaps}
                </a>
              )}
            </SetupRecord>
          );
        }}
      />
      <SetupCollection
        icon="fa-calendar-days"
        title={text.setupPlans}
        items={plans}
        emptyText={text.noSetupPlans}
        renderItem={(plan) => (
          <SetupRecord
            key={plan.id}
            icon="fa-calendar-check"
            title={localizedName(plan, language)}
            subtitle={`${plan.duration || "—"} ${plan.installmentType === "Weekly" ? text.weeks : text.months}`}
          >
            <SetupRecordDetails rows={[
              [text.arabicName, plan.nameAr],
              [text.englishName, plan.nameEn],
              [text.installmentType, plan.installmentType === "Weekly" ? text.weekly : text.monthly],
              [text.duration, plan.duration],
              [text.status, plan.isDeleted ? text.archived : text.active],
              [text.createdAt, dateValue(plan.createdAt)],
              [text.updatedAt, dateValue(plan.updatedAt)]
            ]} />
          </SetupRecord>
        )}
      />
      <SetupCollection
        icon="fa-credit-card"
        title={text.setupPaymentMethods}
        items={paymentMethods}
        emptyText={text.noSetupPaymentMethods}
        renderItem={(method) => (
          <SetupRecord
            key={method.id}
            icon="fa-wallet"
            title={localizedName(method, language)}
            subtitle={method.body}
          >
            <SetupRecordDetails rows={[
              [text.arabicName, method.nameAr],
              [text.englishName, method.nameEn],
              [text.paymentMethodDetails, method.body]
            ]} />
          </SetupRecord>
        )}
      />
    </div>
  );
};

const MerchantProfileCompleteness = ({
  merchant = {},
  text,
  language = "en",
  compact = false,
  details = false
}) => {
  const anchorRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const steps = setupSteps(merchant, text);
  const completed = steps.filter(([, , done]) => done).length;
  const calculated = Math.round((completed / steps.length) * 100);
  const percentage = Number.isFinite(Number(merchant.profileCompleteness))
    ? Number(merchant.profileCompleteness)
    : calculated;

  const updateTooltipPosition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const estimatedHeight = 170;
    const opensAbove = rect.bottom + estimatedHeight > window.innerHeight;
    setTooltipPosition({
      left: Math.min(
        Math.max(rect.left + rect.width / 2, 140),
        Math.max(140, window.innerWidth - 140)
      ),
      top: opensAbove ? rect.top - 8 : rect.bottom + 8,
      transform: opensAbove ? "translate(-50%, -100%)" : "translateX(-50%)"
    });
  }, []);

  useEffect(() => {
    if (!tooltipOpen || details) return undefined;
    updateTooltipPosition();
    window.addEventListener("resize", updateTooltipPosition);
    window.addEventListener("scroll", updateTooltipPosition, true);
    return () => {
      window.removeEventListener("resize", updateTooltipPosition);
      window.removeEventListener("scroll", updateTooltipPosition, true);
    };
  }, [details, tooltipOpen, updateTooltipPosition]);

  const progress = (
    <span className="merchant-completeness-value">
      <strong>{percentage}%</strong>
      <span aria-hidden="true">
        <i style={{ width: `${percentage}%` }} />
      </span>
    </span>
  );

  if (details) {
    return (
      <div
        className="merchant-setup-details"
        aria-label={`${text.profileCompleteness}: ${percentage}%`}
      >
        {progress}
        <div className="merchant-setup-details-list">
          <SetupSteps steps={steps} text={text} />
        </div>
        <MerchantSetupCollections
          merchant={merchant}
          text={text}
          language={language}
        />
      </div>
    );
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={`merchant-completeness${compact ? " is-compact" : ""}`}
        tabIndex="0"
        aria-label={`${text.profileCompleteness}: ${percentage}%`}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
      >
        {progress}
      </span>
      {tooltipOpen && tooltipPosition && createPortal(
        <span
          className="merchant-completeness-tooltip is-portal"
          role="tooltip"
          style={tooltipPosition}
        >
          <strong>{text.merchantSetupProgress}</strong>
          <SetupSteps steps={steps} text={text} />
        </span>,
        document.body
      )}
    </>
  );
};

export default MerchantProfileCompleteness;
